import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { FileSignature, ShieldCheck, ShieldAlert } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { salesApi, apiError } from '../../salesApi';
import { useT } from '../../salesI18n';

export const PAYMENT_METHODS = ['check', 'bank_check', 'credit_card', 'cash', 'financing'];
export const leadSafeFor = (year) => (year === '' || year == null ? 'unknown' : Number(year) < 1978 ? 'yes' : 'no');

const empty = (lead, proposal) => ({ payment_method: '', amount_financed: '', deposit_received: proposal?.deposit_amount ?? '', notes: '', second_buyer_name: '', secondary_phone: '', secondary_email: '', year_built: lead?.year_built ?? '' });

// Dados da capa do contrato (Agreement Document and Payment Terms). contract = null → cria; contract em rascunho → edita.
export const ContractCreateDialog = ({ open, onOpenChange, lead, proposal, contract, onSaved }) => {
  const { t } = useT();
  const [f, setF] = useState(empty(lead, proposal));
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  useEffect(() => {
    if (!open) return;
    const d = contract?.details || {};
    setF({ ...empty(lead, proposal), ...Object.fromEntries(Object.entries(d).map(([k, v]) => [k, v ?? ''])), year_built: contract?.year_built ?? lead?.year_built ?? '' });
  }, [open, contract, lead, proposal]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const num = (v) => (v === '' || v == null ? null : Number(v));
    const body = { payment_method: f.payment_method || null, amount_financed: num(f.amount_financed), deposit_received: num(f.deposit_received), notes: f.notes || null,
      second_buyer_name: f.second_buyer_name || null, secondary_phone: f.secondary_phone || null, secondary_email: f.secondary_email || null, year_built: num(f.year_built) };
    try {
      const { data } = contract ? await salesApi.put(`/contracts/${contract.id}`, body) : await salesApi.post(`/proposals/${proposal.id}/contract`, body);
      toast.success(contract ? t.saved : t.contractCreated);
      onSaved?.(data);
      onOpenChange(false);
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const ls = leadSafeFor(f.year_built);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92dvh] overflow-y-auto sales-kanban-scroll" data-testid="contract-create-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><FileSignature className="h-5 w-5 text-teal-700" /> {t.contractDetails}{contract ? ` · ${contract.number}` : ''}</DialogTitle>
          <p className="text-xs text-slate-500">{t.contractDetailsHint}</p>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold text-slate-600">{t.paymentMethod}</Label>
              <Select value={f.payment_method || 'none'} onValueChange={(v) => set('payment_method', v === 'none' ? '' : v)}>
                <SelectTrigger className="h-11 mt-1" data-testid="contract-payment-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t.notInformed}</SelectItem>
                  {PAYMENT_METHODS.map((m) => <SelectItem key={m} value={m} data-testid={`contract-payment-opt-${m}`}>{t.paymentMethods[m]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-600">{t.depositReceived}</Label>
              <Input type="number" min="0" step="0.01" value={f.deposit_received} onChange={(e) => set('deposit_received', e.target.value)} className="h-11 mt-1 font-mono" data-testid="contract-deposit-received" />
            </div>
            {f.payment_method === 'financing' && (
              <div>
                <Label className="text-xs font-semibold text-slate-600">{t.amountFinanced}</Label>
                <Input type="number" min="0" step="0.01" value={f.amount_financed} onChange={(e) => set('amount_financed', e.target.value)} className="h-11 mt-1 font-mono" data-testid="contract-amount-financed" />
              </div>
            )}
            <div>
              <Label className="text-xs font-semibold text-slate-600">{t.yearBuilt}</Label>
              <Input type="number" min="1700" max={new Date().getFullYear() + 1} value={f.year_built} onChange={(e) => set('year_built', e.target.value)} className="h-11 mt-1 font-mono" placeholder="1985" data-testid="contract-year-built" />
            </div>
          </div>
          <div className={`flex items-start gap-2 rounded-2xl px-3 py-2 text-xs font-semibold ${ls === 'no' ? 'bg-slate-50 text-slate-600 border border-slate-200' : 'bg-amber-50 text-amber-800 border border-amber-200'}`} data-testid="contract-lead-safe-hint">
            {ls === 'no' ? <ShieldCheck className="h-4 w-4 shrink-0 mt-0.5" /> : <ShieldAlert className="h-4 w-4 shrink-0 mt-0.5" />}
            <span>{ls === 'yes' ? t.leadSafeIncluded : ls === 'no' ? t.leadSafeNotIncluded : t.leadSafeUnknown}<br /><span className="font-normal text-[11px] opacity-80">{t.yearBuiltHint}</span></span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <Label className="text-xs font-semibold text-slate-600">{t.secondBuyer}</Label>
              <Input value={f.second_buyer_name} onChange={(e) => set('second_buyer_name', e.target.value)} className="h-11 mt-1" data-testid="contract-second-buyer" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-600">{t.secondaryPhone}</Label>
              <Input value={f.secondary_phone} onChange={(e) => set('secondary_phone', e.target.value)} className="h-11 mt-1" inputMode="tel" data-testid="contract-secondary-phone" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-600">{t.secondaryEmail}</Label>
              <Input type="email" value={f.secondary_email} onChange={(e) => set('secondary_email', e.target.value)} className="h-11 mt-1" data-testid="contract-secondary-email" />
            </div>
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-600">{t.contractNotes}</Label>
            <Textarea value={f.notes} onChange={(e) => set('notes', e.target.value)} rows={2} className="mt-1" data-testid="contract-notes" />
          </div>
          <p className="text-[11px] text-slate-500" data-testid="contract-package-hint">{t.contractPackage}</p>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" className="h-11" onClick={() => onOpenChange(false)} data-testid="contract-create-cancel">{t.cancel}</Button>
            <Button type="submit" className="h-11 font-semibold" disabled={busy} data-testid="contract-create-submit">{busy ? t.loading : contract ? t.save : t.createContract}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
